import * as Intercom from '@/intercom';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Intercom window type declaration to prevent TypeScript errors
 */
declare global {
    interface IntercomCommand {
        (command: 'boot', settings?: object): void;
        (command: 'shutdown'): void;
        (command: 'update', settings?: object): void;
        (command: 'show'): void;
        (command: 'hide'): void;
        (command: 'trackEvent', eventName: string, metadata?: object): void;
        (command: string, ...args: unknown[]): void;
    }
    
    interface Window {
        Intercom?: IntercomCommand;
    }
}

/**
 * Interface for the Intercom handler component props
 */
export interface IntercomHandlerProps {
    userData: {
        id: string;
        email: string;
        userName: string;
        firstName: string;
        lastName: string;
        clientId: string;
        createdAt: string | number;
    };
    localStorageData: {
        organizationName: string | null;
        plan: string | null;
        orgCreatedAt: string | null;
        monthlyPayment: string | null;
        lastPaymentDate: string | null;
        lastPaymentAmount: string | null;
        orgStatus: string | null;
    };
}

/**
 * Component that handles Intercom initialization and responds to route changes
 * 
 * This component uses the location hook to detect path changes and conditionally
 * shows or hides Intercom based on whether the current path is a donor-facing page
 * (paths starting with /frontend).
 */
const IntercomHandler: React.FC<IntercomHandlerProps> = ({ userData, localStorageData }) => {
    // useLocation hook will cause this component to re-render on route changes
    const location = useLocation();
    
    // Check if path starts with /frontend - which is legacy naming for widget and donor-facing views
    // We don't want to show Intercom chat widget on donor-facing pages
    const isFrontendPath = location.pathname.startsWith('/frontend');
    
    useEffect(() => {
        // Initialize or update Intercom based on current path
        if (!isFrontendPath) {
            // Load Intercom script if not already loaded
            if (typeof window.Intercom !== 'function') {
                Intercom.load();
            }
            
            // Fix: isEmpty() doesn't properly evaluate numbers, so we'll check directly
            // The logs show userData.id is a number (542), so isEmpty() may not work as expected
            const isAuthenticated = Boolean(userData.email) && Boolean(userData.id);
            console.log('userData', userData);
            console.log('isAuthenticated', isAuthenticated);

            
            // Boot with appropriate configuration based on authentication state
            isAuthenticated
                ? Intercom.boot(getIntercomConfig())
                : Intercom.boot();
        } else if (typeof window.Intercom === 'function') {
            // If on frontend path but Intercom is loaded, shut it down
            Intercom.shutdown();
        }
        
        // Helper function to get Intercom config
        function getIntercomConfig() {
            return {
                email: userData.email,
                user_id: userData.id,
                userName: userData.userName,
                client_id: userData.clientId,
                organization: localStorageData.organizationName ?? undefined,
                created_at: userData.createdAt,
                signed_up: userData.createdAt,
                company: {
                    id: userData.clientId,
                    name: localStorageData.organizationName ?? undefined,
                    plan: localStorageData.plan ?? undefined,
                    created_at: localStorageData.orgCreatedAt ?? undefined,
                    monthly_spend: localStorageData.monthlyPayment ?? undefined,
                    last_payment_date: localStorageData.lastPaymentDate ?? undefined,
                    last_payment_amount: `$${localStorageData.lastPaymentAmount ?? '0'}`,
                    organization_status: localStorageData.orgStatus ?? undefined,
                }
            };
        }
    }, [location.pathname, userData, localStorageData, isFrontendPath]);
    
    return null; // This component doesn't render anything
};

export default IntercomHandler;